import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { FaLinkedin, FaInstagram, FaFacebook, FaTiktok } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import NewsletterPopup from "../NewsletterPopup/NewsletterPopup";
import config from "../../config";
import "./Footer.css";

const Footer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const [email, setEmail] = useState("");
  const [popup, setPopup] = useState({ show: false, message: "", type: "" });

  const handleNewsletterSubmit = async () => {
    if (!email.trim()) {
      setPopup({
        show: true,
        message: "Please enter a valid email address.",
        type: "error",
      });
      return;
    }

    try {
      const response = await axios.post(`${config.apiBaseUrl}/api/newsletter`, {
        email,
      });

      if (response.data.exists) {
        setPopup({
          show: true,
          message: response.data.message,
          type: "error",
        });
      } else {
        setPopup({
          show: true,
          message: response.data.message,
          type: "success",
        });
        setEmail("");
      }
    } catch (error) {
      setPopup({
        show: true,
        message:
          error.response?.data?.message ||
          "An error occurred. Please try again later.",
        type: "error",
      });
    }
  };

  const closePopup = () => {
    setPopup({ show: false, message: "", type: "" });
  };

  return (
    <Box
      component="footer"
      className="footer"
    >
      <Box className="footer-container">
        <Box className="footer-social">
          <Typography variant="h6" color="text.secondary">
            STAY CONNECTED WITH US
          </Typography>
          <Box className="social-icons">
            <IconButton
              href="https://www.linkedin.com/company/airkitchenz/"
              className="social-icon"
              color="inherit"
            >
              <FaLinkedin />
            </IconButton>
            <IconButton
              href="https://www.instagram.com/airkitchenz/"
              className="social-icon"
              color="inherit"
            >
              <FaInstagram />
            </IconButton>
            <IconButton
              href="https://www.facebook.com/AirKitchenz"
              className="social-icon"
              color="inherit"
            >
              <FaFacebook />
            </IconButton>
            <IconButton
              href="https://x.com/AirKitchenz"
              className="social-icon"
              color="inherit"
            >
              <FaXTwitter />
            </IconButton>
            <IconButton
              href="https://www.tiktok.com/@airkitchenzofficial"
              className="social-icon"
              color="inherit"
            >
              <FaTiktok />
            </IconButton>
          </Box>
          {!isMobile && <Divider orientation="vertical" className="divider" />}
          <Box className="join-us">
            <Typography variant="h6" color="text.secondary" sx={{ mr: 2 }}>
              Newsletter
            </Typography>
            <Box className="email-subscribe">
              <TextField
                variant="outlined"
                size="small"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "background.paper",
                    pr: 0,
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <Button
                      variant="contained"
                      onClick={handleNewsletterSubmit}
                      sx={{
                        bgcolor: "secondary.main",
                        "&:hover": { bgcolor: "secondary.dark" },
                        borderRadius: `0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0`,
                        minWidth: "40px",
                        p: 1,
                      }}
                    >
                      ➔
                    </Button>
                  ),
                }}
              />
            </Box>
          </Box>
        </Box>
        <Divider sx={{ bgcolor: "text.secondary" }} />
        <Box className="footer-links">
          <Box className="footer-logo">
            <img
              src="https://airkitchenz-assets.s3.us-west-1.amazonaws.com/assets/footer-logo.png"
              alt="AirKitchenz Logo"
            />
          </Box>
          <Box className="footer-section">
            <Typography variant="h6" color="text.secondary" gutterBottom>
              AirKitchenz
            </Typography>
            <Box component="ul">
              <li>
                <Link to="/aboutus">About AirKitchenz</Link>
              </li>
              <li>
                <Link to="/blogs">Blog</Link>
              </li>
              <li>
                <Link to="/faqs">FAQs</Link>
              </li>
              <li>
                <Link to="/careerspage">Careers</Link>
              </li>
              <li>
                <Link to="/contactus">Contact Us</Link>
              </li>
            </Box>
          </Box>
          <Box className="footer-section">
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Business
            </Typography>
            <Box component="ul">
              <li>
                <a href="/browse">Find a Kitchen</a>
              </li>
              <li>
                <a href="/affiliate">Affiliate Program</a>
              </li>
              <li>
                <a href="/aibuilder">AI Business builder</a>
              </li>
              <li>
                <a href="/features-services">Features and Services</a>
              </li>
              <li>
                <a href="/kitchenwaitinglist">Join Waitlist</a>
              </li>
            </Box>
          </Box>
          <Box className="footer-section">
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Social Media
            </Typography>
            <Box component="ul">
              <li>
                <a href="https://www.linkedin.com/company/airkitchenz/">
                  LinkedIn
                </a>
              </li>
              <li>
                <a href="https://www.instagram.com/airkitchenz/">Instagram</a>
              </li>
              <li>
                <a href="https://www.facebook.com/AirKitchenz">Facebook</a>
              </li>
              <li>
                <a href="https://x.com/AirKitchenz">Twitter</a>
              </li>
              <li>
                <a href="https://www.tiktok.com/@airkitchenzofficial">Tiktok</a>
              </li>
            </Box>
          </Box>
        </Box>
        <Divider sx={{ bgcolor: "text.secondary" }} />
        <Box className="footer-bottom">
          <Typography variant="body2" color="text.secondary">
            &copy; 2025 AirKitchenz. All rights reserved.
          </Typography>
          <Box className="footer-policy">
            <Link to="/termsofuse">
              <Typography
                variant="body2"
                color="text.secondary"
                component="span"
              >
                Terms of Use
              </Typography>
            </Link>
            <Link to="/privacypolicy">
              <Typography
                variant="body2"
                color="text.secondary"
                component="span"
                sx={{ ml: 2 }}
              >
                Privacy Policy
              </Typography>
            </Link>
          </Box>
        </Box>
        {popup.show && (
          <NewsletterPopup
            message={popup.message}
            type={popup.type}
            onClose={closePopup}
          />
        )}
      </Box>
    </Box>
  );
};

export default Footer;
